import React, { SetStateAction, useState } from "react";

import { NeutralColor } from "node-library/dist/colors";
import {
	ReceivingCondition,
	Severity,
	TurnaroundTime,
	WeightUnit,
} from "node-library/dist/constants";
import { createUUID } from "node-library/dist/helpers/request_helper";
import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestPackage,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSample,
	FrontendSampleAdditionalInfoMapping,
	FrontendSampleInstance,
	FrontendSampleInstanceAdditionalInfoMapping,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendSampleTestInformation,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";
import { StepsBar } from "../../../common-components/steps-bar";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import StepFourSelectTestOptions from "./stepFourSelectTestOptions";
import StepSixSampleInfoConfirm from "./stepSixSampleInfoConfirm";

import { BLANK_SAMPLE_TEST_INFO } from "./samplePackages";
import StepThreeSampleInstanceInfo from "./stepThreeSampleInstanceInfo";
import StepTwoSampleInfo from "./stepTwoSampleInfo";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;

	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;
	orderTestCategory: FrontendCompanyTestCategory;

	organizationId: string;
	organizationSamples: Array<FrontendSample>;
	setOrganizationSamples: React.Dispatch<
		React.SetStateAction<FrontendSample[]>
	>;
	sampleInstanceToUpdate: FrontendSampleInstance | null;

	sampleSpecifications: Array<FrontendSampleSpecification>;
	setSampleSpecifications: React.Dispatch<
		React.SetStateAction<FrontendSampleSpecification[]>
	>;
	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	setSpecDirectories: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecificationDirectory>>
	>;

	addSample: (newSample: FrontendSampleInstance) => void;
	updateSample: (sampleToUpdate: FrontendSampleInstance) => void;
	goBackToOrderPage: () => void;
	discardSample: () => void;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function NewSample(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const setUnsavedChanges = props.setUnsavedChanges;

	const company = props.company;
	const sampleInstanceToUpdate = props.sampleInstanceToUpdate;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(1);

	const [selectedSampleId, setSelectedSampleId] = useState<string>(
		sampleInstanceToUpdate?.sample.sampleId || "",
	);
	const [isNewSample, setIsNewSample] = useState<boolean>(
		sampleInstanceToUpdate ? sampleInstanceToUpdate.sample.isNewSample : true,
	);

	const selectedSample: FrontendSample | undefined =
		props.organizationSamples.find((sample) => {
			return sample.sampleId === selectedSampleId;
		});

	//: sample info
	const [lotCode, setLotCode] = useState<string>(
		sampleInstanceToUpdate?.sample?.lotCode || "",
	);
	const [sampleName, setSampleName] = useState<string>(
		sampleInstanceToUpdate?.sample?.sampleName || "",
	);
	const [sampleDescription, setSampleDescription] = useState<string>(
		sampleInstanceToUpdate?.sample?.sampleDescription || "",
	);
	const [matrix, setMatrix] = useState<string>(
		sampleInstanceToUpdate?.sample?.isCustomMatrix
			? sampleInstanceToUpdate?.sample?.matrix
			: "",
	);

	const [selectedMatrixId, setSelectedMatrixId] = useState<string>(
		sampleInstanceToUpdate?.sample?.isCustomMatrix
			? ""
			: sampleInstanceToUpdate?.sample.matrixId || "",
	);

	const selectedMatrix: FrontendCompanySampleMatrix | undefined =
		props.orderTestCategory.sampleMatrices.find((matrix) => {
			return matrix.matrixId === selectedMatrixId;
		});
	const sampleCategory: string = selectedMatrix
		? selectedMatrix.sampleCategory
		: "";

	const [selectedPackageId, setSelectedPackageId] = useState<string>(
		sampleInstanceToUpdate ? sampleInstanceToUpdate.packageId || "" : "",
	);
	const selectedPackage: FrontendCompanyTestPackage | undefined =
		props.orderTestCategory.testPackages.find((pack) => {
			return pack.packageId === selectedPackageId;
		});

	//sample additional info
	const [sampleAdditionalInfos, setSampleAdditionalInfos] = useState<
		FrontendSampleAdditionalInfoMapping[]
	>(sampleInstanceToUpdate?.sample?.sampleAdditionalInfos || []);

	const [sampleInstanceAdditionalInfos, setSampleInstanceAdditionalInfos] =
		useState<FrontendSampleInstanceAdditionalInfoMapping[]>(
			sampleInstanceToUpdate?.sampleInstanceAdditionalInfos || [],
		);

	const [sampleTestInfo, setSampleTestInfo] =
		useState<FrontendSampleTestInformation>(
			sampleInstanceToUpdate && sampleInstanceToUpdate.sampleTestInfo
				? sampleInstanceToUpdate.sampleTestInfo
				: BLANK_SAMPLE_TEST_INFO,
		);

	//turnaroundTime
	const [turnaroundTime, setTurnaroundTime] = useState<TurnaroundTime>(
		sampleInstanceToUpdate && sampleInstanceToUpdate.sampleTestInfo
			? sampleInstanceToUpdate.sampleTestInfo?.turnaroundTime
			: TurnaroundTime.STANDARD,
	);

	//: weights
	const [weight, setWeight] = useState<string>(
		sampleInstanceToUpdate?.weight || "",
	);
	const [weightUnit, setWeightUnit] = useState<WeightUnit>(
		sampleInstanceToUpdate?.weightUnit || WeightUnit.GRAMS,
	);
	const [isRetest, setIsRetest] = useState<boolean>(
		sampleInstanceToUpdate?.isRetest || false,
	);
	const [isAdditionalTest, setIsAdditionalTest] = useState<boolean>(
		sampleInstanceToUpdate?.isAdditionalTest || false,
	);

	const [treatment, setTreatment] = useState<boolean>(
		sampleInstanceToUpdate?.treatment || false,
	);

	const [packageReceivedTime, setPackageReceivedTime] = useState<Date | null>(
		sampleInstanceToUpdate?.receivedTime || new Date(),
	);

	const [expectedCompletionDate, setExpectedCompletionDate] =
		useState<Date | null>(
			sampleInstanceToUpdate?.expectedCompletionTime || null,
		);

	const [receivingCondition, setReceivingCondition] =
		useState<ReceivingCondition>(
			sampleInstanceToUpdate?.receivingCondition || ReceivingCondition.AMBIENT,
		);

	const [selectedSpecificationId, setSelectedSpecificationId] = useState<
		string | undefined
	>(sampleInstanceToUpdate?.specification?.specificationId);
	const [otherInstructions, setOtherInstructions] = useState<string>(
		sampleInstanceToUpdate?.otherInstructions || "",
	);

	const selectedSpecificationFile: FrontendSampleSpecification | undefined =
		props.sampleSpecifications.find((spec) => {
			return spec.specificationId == selectedSpecificationId;
		});

	function addOrUpdateSample() {
		const sample: FrontendSample = isNewSample
			? {
					sampleId: selectedSample?.sampleId || createUUID(),
					testCategoryId: props.orderTestCategory.testCategoryId,
					lotCode,
					sampleName,
					sampleDescription,
					matrix,
					matrixId: selectedMatrixId,
					sampleAdditionalInfos,
					isCustomMatrix: false,
					isNewSample,
				}
			: (selectedSample as FrontendSample);
		if (isNewSample && !selectedSample) {
			props.setOrganizationSamples((prevSamples) => [
				...prevSamples, // Spread the previous licenses to preserve them
				sample, // Add the new license to the array
			]);
		}
		if (sampleInstanceToUpdate) {
			const sampleInstance: FrontendSampleInstance = {
				sampleInstanceId: sampleInstanceToUpdate.sampleInstanceId,
				sample,
				packageId: selectedPackageId,

				weight,
				weightUnit,
				isRetest,
				isAdditionalTest,
				treatment: treatment,
				specification: selectedSpecificationFile,

				otherInstructions,
				sampleTestInfo: sampleTestInfo,
				// showInGallery,
				receivedTime: packageReceivedTime as Date,
				receivingCondition,
				expectedCompletionTime: expectedCompletionDate || undefined,
				pendingReview: false,
				sampleInstanceAdditionalInfos: sampleInstanceAdditionalInfos,
			};
			props.updateSample(sampleInstance);
			openSnackbar(
				translation("SampleUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
		} else {
			const sampleInstance: FrontendSampleInstance = {
				sampleInstanceId: createUUID(),

				sample,
				packageId: selectedPackageId,
				weight,
				weightUnit,
				isRetest,
				isAdditionalTest,
				treatment: treatment,
				specification: selectedSpecificationFile,

				otherInstructions,
				sampleTestInfo: sampleTestInfo,
				// showInGallery,
				receivedTime: packageReceivedTime as Date,
				receivingCondition,
				expectedCompletionTime: expectedCompletionDate || undefined,
				pendingReview: false,
				sampleInstanceAdditionalInfos: sampleInstanceAdditionalInfos,
			};
			props.addSample(sampleInstance);
			openSnackbar(
				translation("SampleAddedToOrder"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
		}
		props.goBackToOrderPage();
	}

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 1:
			currentComponent = (
				<StepTwoSampleInfo
					company={props.company}
					setCompany={props.setCompany}
					user={props.user}
					lotCode={lotCode}
					selectedSampleId={selectedSampleId}
					setSelectedSampleId={setSelectedSampleId}
					isNewSample={isNewSample}
					setIsNewSample={setIsNewSample}
					setLotCode={setLotCode}
					sampleName={sampleName}
					setSampleName={setSampleName}
					sampleDescription={sampleDescription}
					setSampleDescription={setSampleDescription}
					isCustomMatrix={
						sampleInstanceToUpdate?.sample?.isCustomMatrix || false
					}
					matrix={matrix}
					setMatrix={setMatrix}
					selectedMatrixId={selectedMatrixId}
					setSelectedMatrixId={setSelectedMatrixId}
					orderTestCategory={props.orderTestCategory}
					sampleCategory={sampleCategory}
					sampleAdditionalInfos={sampleAdditionalInfos}
					setSampleAdditionalInfos={setSampleAdditionalInfos}
					setSampleTestInfo={setSampleTestInfo}
					setSelectedPackageId={setSelectedPackageId}
					setCurrentPageIndex={setCurrentPageIndex}
					discardSample={props.discardSample}
					setUnsavedChanges={setUnsavedChanges}
					organizationSamples={props.organizationSamples}
					setOrganizationSamples={props.setOrganizationSamples}
					sampleSpecifications={props.sampleSpecifications}
					setSampleSpecifications={props.setSampleSpecifications}
					specDirectories={props.specDirectories}
					setSpecDirectories={props.setSpecDirectories}
					selectedSpecificationId={selectedSpecificationId}
					setSelectedSpecificationId={setSelectedSpecificationId}
					multipleMatchingSamples={
						sampleInstanceToUpdate?.multipleMatchingSamplesWarning || false
					}
					organizationId={props.organizationId}
				/>
			);
			break;
		case 2:
			currentComponent = (
				<StepThreeSampleInstanceInfo
					orderTestCategory={props.orderTestCategory}
					sampleName={sampleName}
					lotCode={lotCode}
					sampleMatrix={
						selectedMatrix ? `${selectedMatrix?.subMatrix}` : matrix
					}
					weight={weight}
					setWeight={setWeight}
					weightUnit={weightUnit}
					setWeightUnit={setWeightUnit}
					turnaroundTime={turnaroundTime}
					setTurnaroundTime={setTurnaroundTime}
					setCurrentPageIndex={setCurrentPageIndex}
					setUnsavedChanges={setUnsavedChanges}
					packageReceivedTime={packageReceivedTime}
					setPackageReceivedTime={setPackageReceivedTime}
					expectedCompletionDate={expectedCompletionDate}
					setExpectedCompletionDate={setExpectedCompletionDate}
					receivingCondition={receivingCondition}
					setReceivingCondition={setReceivingCondition}
					sampleInstanceAdditionalInfos={sampleInstanceAdditionalInfos}
					setSampleInstanceAdditionalInfos={setSampleInstanceAdditionalInfos}
					isNewSample={isNewSample}
					isRetest={isRetest}
					setIsRetest={setIsRetest}
					isAdditionalTest={isAdditionalTest}
					setIsAdditionalTest={setIsAdditionalTest}
					selectedSample={selectedSample}
				/>
			);
			break;
		case 3:
			currentComponent = (
				<StepFourSelectTestOptions
					company={company}
					orderTestCategory={props.orderTestCategory}
					sampleName={sampleName}
					lotCode={lotCode}
					sampleMatrix={selectedMatrix}
					turnaroundTime={turnaroundTime}
					otherInstructions={otherInstructions}
					setOtherInstructions={setOtherInstructions}
					setCurrentPageIndex={setCurrentPageIndex}
					treatment={treatment}
					setTreatment={setTreatment}
					selectedPackage={selectedPackage}
					setSelectedPackageId={setSelectedPackageId}
					sampleTestInfo={sampleTestInfo}
					setSampleTestInfo={setSampleTestInfo}
					// showInGallery={showInGallery}
					// setShowInGallery={setShowInGallery}
					setUnsavedChanges={setUnsavedChanges}
					isRetest={isRetest}
				/>
			);
			break;
		case 4:
			currentComponent = (
				<StepSixSampleInfoConfirm
					company={company}
					setCompany={props.setCompany}
					user={props.user}
					orderTestCategory={props.orderTestCategory}
					sampleName={sampleName}
					lotCode={lotCode}
					sampleDescription={sampleDescription}
					sampleMatrix={selectedMatrix}
					sampleAdditionalInfos={sampleAdditionalInfos}
					sampleInstanceAdditionalInfos={sampleInstanceAdditionalInfos}
					selectedPackage={selectedPackage}
					sampleTestInfo={sampleTestInfo}
					weight={weight}
					isRetest={isRetest}
					isAdditionalTest={isAdditionalTest}
					treatment={treatment}
					receivingCondition={receivingCondition}
					packageReceivedTime={packageReceivedTime as Date}
					expectedCompletionDate={expectedCompletionDate as Date}
					selectedSpecificationFile={selectedSpecificationFile}
					setSampleSpecifications={props.setSampleSpecifications}
					otherInstructions={otherInstructions}
					weightUnit={weightUnit}
					specDirectories={props.specDirectories}
					// showInGallery={showInGallery}
					setCurrentPageIndex={setCurrentPageIndex}
					addOrUpdateSample={addOrUpdateSample}
				/>
			);
			break;
		case 4:
			break;
	}

	const headerItems = [
		translation("AddSampleDetails"),
		translation("RecordReceivingCondition"),
		translation("SelectServices"),
		translation("ConfirmInformation"),
	];
	return (
		<div id="new-sample-content">
			<div id="new-sample-header">
				<StepsBar
					items={headerItems}
					currentItem={currentPageIndex - 1}
					setCurrentItem={setCurrentPageIndex}
				></StepsBar>
			</div>
			<hr style={{ width: "100%", color: NeutralColor.NEUTRAL_GREY_200 }}></hr>
			<div id="new-sample-body" style={{ margin: "60px 0px" }}>
				{currentComponent}
			</div>
		</div>
	);
}
